import { LogEntry, LogFilters, LogResponse } from '../types'
import {
  buildEsQuery,
  ES_MAX_RESULT_WINDOW,
  ESSearchResponse,
  extractService,
  totalFromResponse,
  transformHit,
} from './elasticQuery'

interface EnvConfig {
  ELASTIC_BASE_URL?: string
  ELASTIC_API_KEY?: string
}

function readEnv(): EnvConfig {
  if (typeof window === 'undefined') return {}
  const w = window as unknown as { _env_?: EnvConfig }
  return w._env_ ?? {}
}

function esBase(): string {
  const base = readEnv().ELASTIC_BASE_URL ?? ''
  if (base.length === 0) return ''
  if (
    typeof window !== 'undefined' &&
    window.location.hostname === 'localhost'
  ) {
    return '/elastic'
  }
  return base.replace(/\/$/, '')
}

function esHeaders(): HeadersInit {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' }
  const key = readEnv().ELASTIC_API_KEY ?? ''
  if (key.length > 0) headers.Authorization = `ApiKey ${key}`
  return headers
}

function isElasticEnabled(): boolean {
  return esBase().length > 0
}

export async function fetchLogs(filters: LogFilters): Promise<LogResponse> {
  const response = isElasticEnabled()
    ? await fetchLogsFromElastic(filters)
    : await fetchLogsFromMockApi(filters)
  return response
}

export async function fetchSubsystems(): Promise<string[]> {
  if (isElasticEnabled()) return await fetchSubsystemsFromElastic()
  return await fetchSubsystemsFromMockApi()
}

export async function fetchUsers(): Promise<string[]> {
  if (isElasticEnabled()) return await fetchUsersFromElastic()
  return await fetchUsersFromMockApi()
}

export async function fetchStates(): Promise<string[]> {
  if (isElasticEnabled()) return await fetchStatesFromElastic()
  return await fetchStatesFromMockApi()
}

// Elasticsearch returns a structured error body ({ error: { type, reason } })
// on 4xx/5xx. Surface that reason so failures are diagnosable instead of a bare
// "400 Bad Request" that hides which clause the cluster rejected.
async function esError(res: Response, context: string): Promise<Error> {
  let detail = ''
  try {
    const body = (await res.json()) as {
      error?: { type?: string; reason?: string }
    }
    const reason = body.error?.reason
    const type = body.error?.type
    if (typeof reason === 'string' && reason.length > 0) {
      detail = type != null ? ` — ${type}: ${reason}` : ` — ${reason}`
    }
  } catch {
    // Body was not JSON (or already consumed); fall back to status text only.
  }
  return new Error(`${context}: ${res.status} ${res.statusText}${detail}`)
}

async function fetchLogsFromElastic(filters: LogFilters): Promise<LogResponse> {
  const body = buildEsQuery(filters)
  const res = await fetch(`${esBase()}/constellation-*/_search`, {
    method: 'POST',
    headers: esHeaders(),
    body: JSON.stringify(body),
  })
  if (!res.ok) {
    throw await esError(res, 'Failed to fetch logs from Elasticsearch')
  }
  const json = (await res.json()) as ESSearchResponse
  const data: LogEntry[] = json.hits.hits.map(transformHit)

  const total = totalFromResponse(json)
  // Elasticsearch can only serve the first ES_MAX_RESULT_WINDOW hits, so cap
  // the page count to what is reachable — otherwise the UI offers pages whose
  // deep-pagination request fails and strands the user.
  const reachable = Math.min(total, ES_MAX_RESULT_WINDOW)
  const totalPages = total === 0 ? 0 : Math.ceil(reachable / filters.limit)

  return {
    data,
    pagination: {
      page: filters.page,
      limit: filters.limit,
      total,
      totalPages,
    },
  }
}

async function fetchSubsystemsFromElastic(): Promise<string[]> {
  const res = await fetch(
    `${esBase()}/_cat/indices/constellation-*?format=json`,
    { headers: esHeaders() },
  )
  if (!res.ok) {
    throw new Error(
      `Failed to fetch subsystems from Elasticsearch: ${res.status} ${res.statusText}`,
    )
  }
  const rows = (await res.json()) as Array<{ index: string }>
  return [
    ...new Set(rows.map(r => extractService(r.index).toUpperCase())),
  ].sort()
}

async function fetchLogsFromMockApi(filters: LogFilters): Promise<LogResponse> {
  const params = new URLSearchParams()
  params.set('search', filters.search)
  params.set('level', filters.level)
  for (const subsystem of filters.subsystems) {
    params.append('subsystem', subsystem)
  }
  params.set('datePreset', filters.datePreset)
  if (filters.dateFrom !== null && filters.dateFrom.length > 0) {
    params.set('dateFrom', filters.dateFrom)
  }
  if (filters.dateTo !== null && filters.dateTo.length > 0) {
    params.set('dateTo', filters.dateTo)
  }
  for (const eventType of filters.eventTypes) {
    params.append('eventType', eventType)
  }
  for (const user of filters.users) params.append('user', user)
  for (const state of filters.states) params.append('state', state)
  params.set('traceId', filters.traceId)
  params.set('requestId', filters.requestId)
  params.set('sortField', filters.sortField)
  params.set('sortOrder', filters.sortOrder)
  params.set('page', String(filters.page))
  params.set('limit', String(filters.limit))

  const response = await fetch(`/api/logs?${params.toString()}`)
  if (!response.ok) {
    throw new Error(
      `Failed to fetch logs: ${response.status} ${response.statusText}`,
    )
  }
  return (await response.json()) as LogResponse
}

async function fetchSubsystemsFromMockApi(): Promise<string[]> {
  const response = await fetch('/api/logs/subsystems')
  if (!response.ok) {
    throw new Error(
      `Failed to fetch subsystems: ${response.status} ${response.statusText}`,
    )
  }
  return (await response.json()) as string[]
}

interface ESUsersAggResponse {
  aggregations?: {
    users?: { buckets: Array<{ key: string }> }
  }
}

async function fetchUsersFromElastic(): Promise<string[]> {
  const body = {
    size: 0,
    aggs: {
      users: {
        terms: {
          field: 'fields.Username.keyword',
          size: 1000,
          order: { _key: 'asc' },
        },
      },
    },
  }
  const res = await fetch(`${esBase()}/constellation-*/_search`, {
    method: 'POST',
    headers: esHeaders(),
    body: JSON.stringify(body),
  })
  if (!res.ok) {
    throw new Error(
      `Failed to fetch users from Elasticsearch: ${res.status} ${res.statusText}`,
    )
  }
  const json = (await res.json()) as ESUsersAggResponse
  const buckets = json.aggregations?.users?.buckets ?? []
  return buckets
    .map(b => b.key)
    .filter(key => typeof key === 'string' && key.length > 0)
}

async function fetchUsersFromMockApi(): Promise<string[]> {
  const response = await fetch('/api/logs/users')
  if (!response.ok) {
    throw new Error(
      `Failed to fetch users: ${response.status} ${response.statusText}`,
    )
  }
  return (await response.json()) as string[]
}

interface ESStatesAggResponse {
  aggregations?: {
    states?: { buckets: Array<{ key: string }> }
  }
}

async function fetchStatesFromElastic(): Promise<string[]> {
  const body = {
    size: 0,
    aggs: {
      states: {
        terms: {
          field: 'fields.CommandType.keyword',
          size: 1000,
          order: { _key: 'asc' },
        },
      },
    },
  }
  const res = await fetch(`${esBase()}/constellation-*/_search`, {
    method: 'POST',
    headers: esHeaders(),
    body: JSON.stringify(body),
  })
  if (!res.ok) {
    throw new Error(
      `Failed to fetch states from Elasticsearch: ${res.status} ${res.statusText}`,
    )
  }
  const json = (await res.json()) as ESStatesAggResponse
  const buckets = json.aggregations?.states?.buckets ?? []
  return buckets
    .map(b => b.key)
    .filter(key => typeof key === 'string' && key.length > 0)
}

async function fetchStatesFromMockApi(): Promise<string[]> {
  const response = await fetch('/api/logs/states')
  if (!response.ok) {
    throw new Error(
      `Failed to fetch states: ${response.status} ${response.statusText}`,
    )
  }
  return (await response.json()) as string[]
}
